import { addDays } from 'date-fns';
import i18n from 'config/i18n';
// dayjs, not date-fns: the shared ControlledTimePicker works with Dayjs values.
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import * as yup from 'yup';

import { getLocale } from 'utils/date';

import { WEEKDAYS } from '../../OpeningHours.constants';

const t = i18n.getFixedT(getLocale(), 'common', 'planUnitHome.openingHours');

const isTime = (value: unknown): value is Dayjs =>
  dayjs.isDayjs(value) && value.isValid();

/** A time from the shared picker: a Dayjs, required on an open line. */
const time = yup
  .mixed<Dayjs>()
  .nullable()
  .test('time', t('errors.timeRequired'), function (value) {
    return this.parent.closed || isTime(value);
  });

/** Name and validity, shared with the copy dialog, which asks for exactly these. */
export const scheduleValidityFields = {
  name: yup
    .string()
    .trim()
    .required(t('errors.nameRequired'))
    .max(100, t('errors.tooLong', { max: 100 })),
  validFrom: yup.date().nullable().required(t('errors.validFromRequired')),
  validTo: yup
    .date()
    .nullable()
    .when(
      'validFrom',
      (
        validFrom: Date | null,
        schema: yup.DateSchema<Date | null | undefined>
      ) =>
        validFrom
          ? schema.min(
              addDays(validFrom, 1),
              t('errors.validToBeforeValidFrom')
            )
          : schema
    )
};

/**
 * The quick checks, next to the field they are about. The lengths follow the columns in 353.xml.
 * Rules that span lines - overlap, open and closed on one weekday for one day type - and overlap
 * between schedules are the backend's, which answers with a translated message.
 */
export const schema = yup.object({
  ...scheduleValidityFields,
  description: yup.string().max(255, t('errors.tooLong', { max: 255 })),
  blocks: yup
    .array()
    .min(1)
    .of(
      yup
        .object({
          closed: yup.boolean(),
          startTime: time,
          endTime: time.test(
            'after-start',
            t('errors.endNotAfterStart'),
            (endTime, context) => {
              const { closed, startTime } = context.parent;
              if (closed || !isTime(startTime) || !isTime(endTime)) return true;
              return endTime.format('HH:mm') > startTime.format('HH:mm');
            }
          ),
          description: yup.string().max(255, t('errors.tooLong', { max: 255 })),
          dayTypes: yup.array().min(1, t('errors.dayTypesRequired'))
        })
        .test('any-day', t('errors.daysRequired'), (block) =>
          WEEKDAYS.some((weekday) => block?.[weekday])
        )
    )
});
